import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideRmaFeatureFlagsBrowser } from '@rma/generic/ng/feature-flags';
import { provideCustomImageLoader } from '@rma/generic/ng/images';
import {
  appendToRmaApiUrl,
  appendToRmaBillingApiUrl,
  appendToRmaReviewsApiUrl,
  appendToRmaSurveyApiUrl,
  provideRmaApiUrl,
  provideRmaBaseUrl,
  provideRmaBillingApiUrl,
  provideRmaCdnUrl,
  provideRmaDashboardUrl,
  provideRmaEmailApiUrl,
  provideRmaIdentityUrl,
  provideRmaImagesUrl,
  provideRmaReviewsApiUrl,
  provideRmaSocialApiUrl,
  provideRmaStaticImageUrl,
  provideRmaStaticUrl,
  provideRmaSurveyApiUrl,
} from '@rma/generic/ng/util';
import { provideRmaUser } from '@rma/public/accounts/user';
import { provideRmaTranslation } from '@rma/public/generic/orc/translation';
import { Environment, IS_PRODUCTION, provideRmaLocale } from '@rma/public/generic/util/environment';
import { FeatureFlagUserIdentificationService } from '@rma/public/generic/util/feature-flags';
import {
  createAuthInterceptor,
  createJsonHeaderInterceptor,
  errorDecoratorInterceptor,
  provideRmaTokenProvider,
} from '@rma/public/generic/util/http';
import { httpCacheInterceptor } from '@rma/public/generic/util/http-browser-cache';
import { RmaModalService } from '@rma/public/generic/ui/modal';
import { provideRmaMaterial } from '@rma/public/generic/util/material';
import { provideMetaInitializer } from '@rma/public/generic/util/meta-service';
import { provideRmaGoogleTracking } from '@rma/public/generic/util/tracking/analytics';
import { provideRmaHeap } from '@rma/public/generic/util/tracking/heap';
import { provideRmaTrackJs } from '@rma/public/generic/util/tracking/track-js';
import { provideUuidGeneration } from '@rma/public/generic/util/uuid-generation';
import { provideLowerCaseUrlSerializer } from '@rma/public/site/public/orc-layout';
import { Angulartics2Module } from 'angulartics2';
import { environment as devEnvironment } from '../environment/environment';
import { abortErrorInterceptor } from './interceptors/abort.interceptor';
import { routes } from './routing/app.routes';
import { routerConfig, scrollConfig } from './routing/routing.config';

declare let environment: Environment;

const env = IS_PRODUCTION ? environment : devEnvironment;

export const commonConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserModule),
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    importProvidersFrom(Angulartics2Module.forRoot()),
    provideLowerCaseUrlSerializer(),
    provideMetaInitializer(),
    provideRmaGoogleTracking(),
    provideRmaHeap(),
    provideRmaTokenProvider(),
    provideRmaFeatureFlagsBrowser(env.launchDarklyClientId, FeatureFlagUserIdentificationService),
    provideRmaLocale(),
    provideRmaMaterial(),
    provideRmaTrackJs(),
    provideRmaTranslation(),
    provideRmaUser(),
    RmaModalService,
    provideUuidGeneration(),
    provideRouter(routes, withRouterConfig(routerConfig), withInMemoryScrolling(scrollConfig)),
    provideClientHydration(
      withHttpTransferCacheOptions({
        includePostRequests: true,
        includeRequestsWithAuthHeaders: true,
      }),
      withEventReplay(),
      withIncrementalHydration(),
    ),
    provideCustomImageLoader(),
    provideHttpClient(
      withInterceptors([
        abortErrorInterceptor,
        errorDecoratorInterceptor,
        httpCacheInterceptor({
          blacklist: ['/v2/users/me', '/users/me/*'],
          whitelist: ['/reviews/search/withaggregations'],
        }),
        createAuthInterceptor([appendToRmaApiUrl, appendToRmaReviewsApiUrl, appendToRmaBillingApiUrl, appendToRmaSurveyApiUrl]),
        createJsonHeaderInterceptor([appendToRmaApiUrl, appendToRmaReviewsApiUrl, appendToRmaBillingApiUrl]),
      ]),
      withFetch(),
    ),
    // setting up urls
    provideRmaApiUrl(),
    provideRmaBaseUrl(),
    provideRmaBillingApiUrl(),
    provideRmaCdnUrl(),
    provideRmaDashboardUrl(),
    provideRmaIdentityUrl(),
    provideRmaImagesUrl(),
    provideRmaReviewsApiUrl(),
    provideRmaSocialApiUrl(),
    provideRmaStaticImageUrl(),
    provideRmaStaticUrl(),
    provideRmaSurveyApiUrl(),
    provideRmaEmailApiUrl(),
  ],
};
